import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  TextField,
  InputAdornment,
  Divider,
  Paper,
  Button,
  Grid,
  Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import EmailIcon from '@mui/icons-material/Email';
import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';

// FAQ data organized by categories
const FAQ_DATA = {
  general: {
    title: "General Questions",
    faqs: [
      {
        question: "What is roamzap?",
        answer: "roamzap is an AI-powered travel planning assistant that helps you create personalized travel itineraries. Using advanced AI technology, we generate detailed day-by-day travel plans based on your preferences, including attractions, activities, and dining recommendations."
      },
      {
        question: "How does the token system work?",
        answer: "roamzap uses a token-based system where each itinerary generation costs 1 token. New users receive a welcome bonus of 3 tokens upon signing up. Additional tokens can be purchased through our platform to continue generating and customizing itineraries."
      },
      {
        question: "Do I need to create an account?",
        answer: "You can sign up easily using your Google account. Creating an account allows you to manage your tokens, save your itineraries, and keep track of your travel plans."
      },
      {
        question: "What makes roamzap different from other travel planners?",
        answer: "roamzap uses advanced AI to create highly personalized itineraries that consider your interests, travel style, and preferences. Our platform not only suggests destinations but provides detailed daily schedules with specific activities and timing, making trip planning effortless. More features are coming soon!"
      }
    ]
  },
  planning: {
    title: "Trip Planning",
    faqs: [
      {
        question: "How do I start planning a trip?",
        answer: "To start planning, click 'Create New Trip' and enter your destination and travel dates and specify your preferences and interests in detail. Our AI will use this information to generate a customized itinerary. Each itinerary generation will use 1 token from your account."
      },
      {
        question: "How detailed are the generated itineraries?",
        answer: "Our itineraries provide a day-by-day breakdown of activities, including suggested timings, locations, and brief descriptions. You can view and edit the itinerary further, and the AI will help optimize the schedule based on your changes. No tokens are used for editing the itinerary."
      },
      {
        question: "Can I modify my itinerary after it's generated?",
        answer: "Yes, you can edit your itinerary after generation. You can suggest to add or remove activities, adjust timings, and make other modifications. No changes can be made once your session is over. It can be accessed again by clicking 'My Trips' on the navigation menu."
      },
      {
        question: "How do I save my itinerary?",
        answer: "Your itineraries are automatically saved to your account. You can access them anytime from your dashboard under 'My Trips'. You can also export them for offline viewing and share with friends using the share button which generates a link to the itinerary."
      }
    ]
  },
  tokens: {
    title: "Tokens & Pricing",
    faqs: [
      {
        question: "How many tokens do I need to create an itinerary?",
        answer: "1 token per itinerary generation. No tokens are used for editing the itinerary."
      },
      {
        question: "How do I get more tokens?",
        answer: "You can purchase additional tokens through our platform. We offer various token packages at different price points. New users receive a welcome bonus of tokens upon signing up."
      },
      {
        question: "Do tokens expire?",
        answer: "No, your tokens don't expire. Once purchased, they remain in your account until used. You can track your token balance shown in various places in the app."
      },
      {
        question: "Can I share tokens with others?",
        answer: "Currently, tokens are linked to individual accounts and cannot be transferred between users. Each user needs their own tokens to generate or modify itineraries."
      }
    ]
  },
  technical: {
    title: "Technical Support",
    faqs: [
      {
        question: "What should I do if the AI generation isn't working?",
        answer: "First, check if you have sufficient tokens in your account. If you do and are still experiencing issues, try refreshing your browser. For persistent problems, please contact our support team using the contact Us link in the navigation menu with details of the error."
      },
      {
        question: "Can I access roamzap on mobile devices?",
        answer: "Yes, roamzap is fully responsive and works on both desktop and mobile browsers. You can plan and view your trips on any device with an internet connection. A dedicated mobile app is coming soon!"
      },
      {
        question: "Is my payment information secure?",
        answer: "Yes, all payments are processed securely through Stripe, a leading payment processor. We never store your credit card information directly on our servers."
      }
    ]
  }
};

const FAQsPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedPanel, setExpandedPanel] = useState(null);
  
  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setExpandedPanel(null); // Reset expanded panel when searching
  };
  
  // Filter FAQs based on search term
  const getFilteredFAQs = () => {
    const filteredCategories = {};
    
    if (!searchTerm.trim()) {
      return FAQ_DATA; // Return all FAQs if no search term
    }
    
    // Search through all categories and questions
    Object.entries(FAQ_DATA).forEach(([categoryKey, category]) => {
      const filteredFaqs = category.faqs.filter(
        faq => 
          faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
          faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
      );
      
      if (filteredFaqs.length > 0) {
        filteredCategories[categoryKey] = {
          title: category.title,
          faqs: filteredFaqs
        };
      }
    });
    
    return filteredCategories;
  };
  
  const filteredFAQs = getFilteredFAQs();
  const hasResults = Object.keys(filteredFAQs).length > 0;
  
  // Handle accordion expansion
  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : null);
  };
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 6, mt: { xs: 8, md: 10 } }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box textAlign="center" mb={6}>
            <Typography 
              variant="h3" 
              component="h1" 
              fontWeight="bold"
              gutterBottom
              sx={{ mb: 2 }}
            >
              Frequently Asked Questions
            </Typography>
            <Typography 
              variant="h6" 
              color="text.secondary" 
              sx={{ maxWidth: 700, mx: 'auto', mb: 4 }}
            >
              Find answers to common questions about using roamzap for your travel planning needs.
            </Typography>
            
            {/* Search bar */}
            <TextField
              fullWidth
              placeholder="Search questions..."
              variant="outlined"
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{ 
                maxWidth: 600, 
                mx: 'auto',
                backgroundColor: 'white',
                borderRadius: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          
          {/* FAQ Categories */}
          <Box mb={4}>
            <Grid container spacing={2} justifyContent="center">
              {Object.keys(FAQ_DATA).map((category) => (
                <Grid item key={category}>
                  <Chip 
                    label={FAQ_DATA[category].title} 
                    clickable
                    color={searchTerm ? (filteredFAQs[category] ? "primary" : "default") : "default"}
                    onClick={() => {
                      setSearchTerm('');
                      const firstQuestionId = `${category}-0`;
                      setExpandedPanel(firstQuestionId);
                      setTimeout(() => {
                        document.getElementById(category)?.scrollIntoView({ behavior: 'smooth' });
                      }, 100);
                    }}
                    sx={{ fontWeight: 500 }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
          
          {/* FAQ Accordions */}
          {hasResults ? (
            Object.entries(filteredFAQs).map(([categoryKey, category]) => (
              <Box key={categoryKey} mb={5} id={categoryKey}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  fontWeight="bold" 
                  sx={{ mb: 3, mt: 4 }}
                >
                  {category.title}
                </Typography>
                
                {category.faqs.map((faq, index) => {
                  const panelId = `${categoryKey}-${index}`;
                  return (
                    <Accordion 
                      key={panelId}
                      expanded={expandedPanel === panelId}
                      onChange={handleAccordionChange(panelId)}
                      sx={{ 
                        mb: 2,
                        boxShadow: 'none',
                        '&:before': {
                          display: 'none',
                        },
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: '8px !important',
                        overflow: 'hidden',
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={`panel-${panelId}-content`}
                        id={`panel-${panelId}-header`}
                        sx={{ 
                          backgroundColor: expandedPanel === panelId ? 'rgba(0, 0, 0, 0.03)' : 'transparent',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.02)',
                          }
                        }}
                      >
                        <Typography fontWeight="500">
                          {faq.question}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0, pb: 3, px: 3 }}>
                        <Typography color="text.secondary">
                          {faq.answer}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            ))
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No questions found matching "{searchTerm}".
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Try a different search term or browse our categories above.
              </Typography>
              <Button 
                variant="outlined" 
                onClick={() => setSearchTerm('')}
                sx={{ mx: 1 }}
              >
                Clear Search
              </Button>
            </Box>
          )}
          
          {/* Contact section */}
          <Paper 
            elevation={0} 
            sx={{ 
              p: 4, 
              mt: 6, 
              borderRadius: 3, 
              bgcolor: 'primary.light', 
              color: 'primary.contrastText',
              textAlign: 'center' 
            }}
          >
            <Typography variant="h5" component="h2" fontWeight="bold" gutterBottom>
              Still have questions?
            </Typography>
            <Typography sx={{ mb: 3 }}>
              We're here to help! Reach out to our support team for assistance.
            </Typography>
            <Box>
              <Button 
                variant="contained" 
                color="primary"
                component={RouterLink}
                to="/contact"
                startIcon={<EmailIcon />}
                sx={{ 
                  mx: 1, 
                  bgcolor: 'primary.main', 
                  color: 'white', 
                  '& .MuiButton-startIcon': { color: 'white' },
                  '&:hover': { 
                    bgcolor: 'primary.dark'
                  } 
                }}
              >
                Contact Support
              </Button>
            </Box>
          </Paper>
        </motion.div>
      </Box>
    </Container>
  );
};

export default FAQsPage; 